<template>
  <!-- eslint-disable max-len-->
  <div v-cloak class="organize-course">
    <Loading :active.sync="inProgress" :can-cancel="false" />

    <ChooseProduct :assigned_product="product" />

    <div v-if="product">
      <AddStudents :assigned_students="students" />
    </div>

    <div v-if="product && course && students.length">
      <div class="summary">
        <hr class="mt-3 mb-3" />
        <h2 class="step-title subtitle is-2">
          <div class="step-title-number color-is-purple">
            3.
          </div>
          <div
            class="step-title-left"
            v-text="$t('organize-course.summary.course')"
          />
          <div class="step-title-center color-is-purple">
            <i
              class="fa fa-flag-checkered is-hidden-mobile"
              aria-hidden="true"
            />
          </div>
          <div
            class="step-title-right"
            v-text="$t('organize-course.summary.summary')"
          />
        </h2>

        <div class="columns is-multiline">
          <div class="column is-3">
            <figure class="image is-16by9 has-shadow">
              <img :src="product.images['16by9']" />
            </figure>
          </div>
          <div class="column is-8">
            <div class="columns is-multiline">
              <div class="column is-12">
                <h3 class="title is-2 color-is-primary" v-text="product.name" />
              </div>
              <div class="column is-4 content font-is-5">
                <span>
                  <strong v-text="$t('organize-course.summary.students')" />
                  <strong
                    class="color-is-success"
                    v-text="students.length"
                  /> </span
                ><br />
              </div>
              <div class="column is-4 content font-is-5">
                <span>
                  <strong
                    v-text="$t('organize-course.summary.delivered-by-email')"
                  />
                  <strong
                    class="color-is-success"
                    v-text="delivered_by_email_count"
                  /> </span
                ><br />
              </div>
              <div class="column is-4 content font-is-5">
                <span>
                  <strong
                    v-text="$t('organize-course.summary.delivered-by-tickets')"
                  />
                  <strong
                    class="color-is-success"
                    v-text="delivered_by_tickets_count"
                  />
                </span>
              </div>
              <div
                v-if="delivered_by_tickets_count"
                class="column is-12 content font-is-5"
              >
                <label class="checkbox">
                  <input
                    v-model="send_tickets_by_email"
                    type="checkbox"
                    class="checkbox"
                  />
                  <strong
                    class="font-is-5"
                    v-text="$t('organize-course.summary.send-tickets-by-email')"
                  />
                </label>
              </div>

              <div
                v-if="
                  courses_need_to_order > 0 || hard_copies_need_to_order > 0
                "
                class="column is-12 font-is-5 bt-1"
              >
                <div class="columns">
                  <div class="column is-3">
                    <strong
                      v-text="$t('organize-course.summary.need-to-order')"
                    />
                  </div>
                  <div
                    v-if="courses_need_to_order > 0"
                    class="column is-2"
                    v-html="
                      $tc(
                        'organize-course.summary.courses-need-to-order',
                        courses_need_to_order,
                        { count: courses_need_to_order },
                      )
                    "
                  />
                  <div
                    v-if="hard_copies_need_to_order > 0"
                    class="column"
                    v-html="
                      $tc(
                        'organize-course.summary.hard-copies-to-order',
                        hard_copies_need_to_order,
                        { count: hard_copies_need_to_order },
                      )
                    "
                  />
                  <div class="column">
                    <button
                      :disable="inProgress"
                      class="button is-medium is-purple is-outlined is-fullwidth"
                      type="button"
                      @click.prevent="placeOrder"
                    >
                      <i class="fa fa-shopping-cart" /> &nbsp;&nbsp;
                      <span
                        v-text="$t('organize-course.summary.place-order')"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="courses_need_to_order < 1 && hard_copies_need_to_order < 1"
            class="column is-12 font-is-5"
          >
            <button
              :disable="inProgress"
              class="button is-medium is-purple is-fullwidth"
              type="button"
              @click.prevent="deliver"
            >
              <i class="fa fa-book" /> &nbsp;&nbsp;
              <span
                v-text="
                  $tc(
                    'organize-course.summary.deliver-courses-to-students',
                    students.length,
                  )
                "
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { sumBy, map, filter } from 'lodash';
import MyCoursesApi from 'api/my-courses';
import shop from 'api/shop';
import ChooseProduct from './ChooseProduct';
import AddStudents from './AddStudents';

export default {
  name: 'OrganizeNewCourse',

  components: {
    ChooseProduct,
    AddStudents
  },

  props: {
    new_course_information: { required: false, type: Object, default: null },
    hard_copies_count: { required: false, type: Number, default: 0 }
  },

  data() {
    return {
      inProgress: false,
      myCoursesApi: new MyCoursesApi(),
      course: null,
      product: null,
      send_tickets_by_email: true,
      students: this.new_course_information.students
        ? this.new_course_information.students
        : []
    };
  },

  computed: {
    hard_copies_required() {
      return sumBy(this.students, s => {
        const sum = s.hard_copy_certificate ? 1 : 0;
        return sum;
      });
    },

    hard_copies_available() {
      return this.hard_copies_count;
    },

    hard_copies_need_to_order() {
      return -(this.hard_copies_available - this.hard_copies_required);
    },

    courses_required() {
      return this.students.length;
    },

    courses_available() {
      return this.course ? this.course.count : 0;
    },

    courses_need_to_order() {
      return -(this.courses_available - this.courses_required);
    },

    delivered_by_email_count() {
      return this.students.filter(std => !std.share_by_ticket).length;
    },

    delivered_by_tickets_count() {
      return this.students.filter(std => std.share_by_ticket).length;
    }
  },

  created() {
    VueEventHub.$on('product-is-chosen', this.setProduct);
    VueEventHub.$on('product-deleted', this.unsetProduct);
    VueEventHub.$on('students-added', students => {
      this.students = students;
    });
  },

  mounted() {
    if (this.new_course_information.product) {
      this.setProduct(this.new_course_information.product);
    }
  },

  methods: {
    setProduct(product) {
      this.inProgress = true;
      this.product = product;

      VueEventHub.$emit('assign-product', this.product);

      this.myCoursesApi
        .availableForSharing(product.program_id)
        .then(course => {
          this.course = course;
          this.inProgress = false;
        })
        .catch(() => {
          this.inProgress = false;
        });
    },

    unsetProduct() {
      this.product = null;

      axios.delete('/organize-course');
    },

    placeOrder() {
      this.inProgress = true;
      const makeOrder = async () => {
        await axios.post('/organize-course', {
          new_course_information: {
            course: this.course,
            product: this.product,
            students: this.students
          }
        });

        await shop.emptyCart();

        if (this.courses_need_to_order) {
          await shop.addToCart(this.product.id, this.courses_need_to_order);
        }

        if (this.hard_copies_need_to_order > 0) {
          await shop.addToCart(
            'hard-copy-certificate',
            this.hard_copies_need_to_order,
          );
        }
      };

      makeOrder().then(() => {
        location.href = `/cart?order-success-redirect=${location.href}`;
      });
    },

    deliver() {
      this.inProgress = true;
      const deliverCourses = async () => {
        await axios.post('/organize-course', {
          new_course_information: {
            course: this.course,
            product: this.product,
            students: this.students
          }
        });

        const response = await axios.post('/organize-course/deliver', {
          program_id: this.product.program_id,
          students: this.students
        });

        return response;
      };

      deliverCourses()
        .then(response => {
          let tickets = null;
          if (this.delivered_by_tickets_count) {
            tickets = map(
              filter(response.data.delivered, { type: 'ticket' }),
              'to',
            ).join(',');
          }

          let url = '/organize-course/success';

          if (tickets) {
            url = `${url}/${tickets}`;
          }

          location.href = url;
        })
        .catch(err => {
          let error_message = '';

          if (err.response && err.response.data && err.response.data.message) {
            error_message = err.response.data.message;
          }

          const msg = `Could not deliver courses. ${error_message} Please try again`;

          error(msg);

          Swal.fire({
            title: 'Error!',
            text: msg,
            type: 'error',
            confirmButtonText: 'Reload'
          }).then(() => {
            location.reload();
          });
        });
    }
  }
};
</script>
